import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { ArrowDownTrayIcon, ArrowPathIcon, EyeIcon, KeyIcon, TableCellsIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { Button } from "../../components/forms/Buttons/Button";
import { downloadKeyTable, getVariantKey } from "../../services/bookletPrintService";
import { getApiErrorMessage } from "../../utils/apiError";
import { cardClass, focusRing, iconButtonClass, Spinner } from "../Exams/TestBuilder/ui";
import { settleFocus } from "./print/focus";
import { ConfirmDialog } from "./print/PrintDialogs";
import { Badge, Notice, phoneButtonClass, QuietButton, sectionHeaderClass, sectionTextClass, sectionTitleClass } from "./print/ui";

// What the keys are: a preview of a booklet not frozen now (never activated, or returned to draft, which drops the
// freeze; activation may shuffle the options again), or as frozen.
const introKey = (booklet) => {
  if (!booklet.frozen) return booklet.status === "draft" ? "bts.answer_keys.intro_draft" : "bts.answer_keys.intro_unfrozen";
  return booklet.status === "archived" ? "bts.answer_keys.intro_archived" : "bts.answer_keys.intro_active";
};

// The note under a confirmation of a booklet not frozen: a draft's key (or table of all keys) is a preview, and so is
// an archived booklet's that is not frozen.
const previewNoteKey = (booklet, table) => {
  if (booklet.status === "draft") return table ? "bts.answer_keys.confirm_table_preview_note" : "bts.answer_keys.confirm_preview_note";
  return table ? "bts.answer_keys.confirm_table_preview_note_unfrozen" : "bts.answer_keys.confirm_preview_note_unfrozen";
};

// One subject of an opened key: №, the correct letters, the points. A long heading wraps (up to 120 characters).
function KeySection({ section }) {
  const [t] = useTranslation();
  const answers = section.answers || [];
  const points = answers.reduce((sum, answer) => sum + (Number(answer.points) || 0), 0);
  return (
    <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
      <div className="border-b border-slate-100 px-3 py-2">
        <h5 className="break-words text-sm font-semibold text-slate-900">{section.heading}</h5>
        <p className="text-xs tabular-nums text-slate-500">{t("bts.answer_keys.section_meta", { count: answers.length, points })}</p>
      </div>
      <table className="w-full text-sm">
        <caption className="sr-only">{section.heading}</caption>
        <thead className="bg-slate-50 text-left text-xs font-medium text-slate-500">
          <tr>
            <th scope="col" className="w-14 px-3 py-1.5">
              {t("bts.answer_keys.col_number")}
            </th>
            <th scope="col" className="px-3 py-1.5">
              {t("bts.answer_keys.col_answer")}
            </th>
            <th scope="col" className="w-16 px-3 py-1.5 text-right">
              {t("bts.answer_keys.col_points")}
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {answers.map((answer) => (
            <tr key={answer.n}>
              <td className="px-3 py-1 tabular-nums text-slate-500">{answer.n}</td>
              <td className="px-3 py-1 font-semibold tracking-wide text-slate-900">
                {(answer.letters || []).length ? answer.letters.join(", ") : "—"}
              </td>
              <td className="px-3 py-1 text-right tabular-nums text-slate-700">{answer.points}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function BookletKeysSection({ booklet }) {
  const [t] = useTranslation();
  // Asked about: {type: "key", number, code} or {type: "table"}; kept while the dialog closes.
  const [confirm, setConfirm] = useState({ open: false, type: null });
  // The key shown: {number, code, loading, error, key}.
  const [opened, setOpened] = useState(null);
  const [tableBusy, setTableBusy] = useState(false);
  const genRef = useRef(0);
  const buttonRefs = useRef({});

  const status = booklet.status;
  const revision = booklet.revision;
  // A key read for another state of the booklet is not shown.
  useEffect(() => {
    genRef.current += 1;
    setOpened(null);
  }, [status, revision]);

  const numbers = booklet.variant_numbers || [];
  const codes = booklet.codes || [];
  const sectionsCount = booklet.sections_count ?? booklet.check?.sections?.length ?? 0;
  if (!sectionsCount) return null;

  const openKey = async (number, code) => {
    const gen = ++genRef.current;
    setOpened({ number, code, loading: true, error: null, key: null });
    try {
      const key = await getVariantKey(booklet.uuid, number);
      if (gen === genRef.current) setOpened({ number, code: key?.code || code, loading: false, error: null, key });
    } catch (err) {
      if (gen === genRef.current) setOpened({ number, code, loading: false, error: err, key: null });
    }
  };

  const closeKey = () => {
    const number = opened?.number;
    genRef.current += 1;
    setOpened(null);
    buttonRefs.current[number]?.focus();
  };

  const askKey = (number, code) => setConfirm({ open: true, type: "key", number, code });

  const downloadTable = async () => {
    setTableBusy(true);
    try {
      const { filename } = await downloadKeyTable(booklet);
      toast.success(t("bts.print.downloaded", { name: filename }));
    } catch (err) {
      toast.error(getApiErrorMessage(t, err, "bts.answer_keys.table_error"));
    } finally {
      setTableBusy(false);
    }
  };

  const onConfirm = () => {
    setConfirm((prev) => ({ ...prev, open: false }));
    if (confirm.type === "table") downloadTable();
    else if (confirm.type === "key") {
      const { number } = confirm;
      openKey(number, confirm.code);
      // Asked again from the key's error ("Try again" is gone while the key is read): the focus goes back to the
      // variant's button once the dialog has faded out.
      settleFocus(() => buttonRefs.current[number]);
    }
  };

  const headingId = `booklet-keys-${booklet.uuid}`;
  const panelId = `booklet-key-panel-${booklet.uuid}`;
  return (
    <section aria-labelledby={headingId} className={cardClass}>
      <div className={sectionHeaderClass}>
        <div className="min-w-0">
          <h3 id={headingId} className={sectionTitleClass}>
            <KeyIcon className="h-5 w-5 text-slate-400" aria-hidden="true" />
            {t("bts.answer_keys.title")}
          </h3>
          <p className={sectionTextClass}>{t(introKey(booklet))}</p>
          <p className="mt-1 text-sm text-slate-500">{t("bts.answer_keys.log_note")}</p>
        </div>
        {/* Secondary to the variants' keys below: quiet, in line with the text on phones. */}
        <QuietButton
          Icon={TableCellsIcon}
          text={t("bts.answer_keys.table")}
          onClick={() => setConfirm({ open: true, type: "table" })}
          busy={tableBusy}
          className="shrink-0 self-start max-sm:-ml-3"
        />
      </div>

      {/* The focus stays on the variant's button (aria-expanded); this says when its key is there. Outside the
          body below, whose spacing would count it. */}
      <p className="sr-only" aria-live="polite">
        {opened && !opened.loading && !opened.error ? t("bts.answer_keys.opened_announce", { code: opened.code }) : ""}
      </p>
      <div className="space-y-4 p-4 sm:p-5">
        <ul className="flex flex-wrap gap-2" aria-label={t("bts.answer_keys.variants_label")}>
          {numbers.map((number, index) => {
            const code = codes[index] || String(number);
            const active = opened?.number === number;
            return (
              <li key={number} className="max-sm:w-full">
                {/* Opens the variant's key (after the confirmation: it is logged), and closes it when open. */}
                <button
                  type="button"
                  ref={(node) => {
                    buttonRefs.current[number] = node;
                  }}
                  title={active ? t("bts.answer_keys.close_aria", { code }) : t("bts.answer_keys.open_title", { code })}
                  aria-expanded={active}
                  aria-controls={active ? panelId : undefined}
                  onClick={() => (active ? closeKey() : askKey(number, code))}
                  className={`inline-flex h-10 items-center gap-2 rounded-lg px-3.5 text-sm font-medium ring-1 ring-inset transition duration-200 max-sm:h-11 max-sm:w-full max-sm:justify-center ${focusRing} ${
                    active
                      ? "bg-slate-900 text-white ring-slate-900"
                      : "bg-white text-slate-700 shadow-sm ring-slate-300 hover:bg-slate-50 hover:text-slate-900"
                  }`}
                >
                  <KeyIcon className={`h-4 w-4 ${active ? "text-white/80" : "text-amber-600"}`} aria-hidden="true" />
                  <span className="font-semibold tabular-nums">{code}</span>
                  <span className={active ? "text-white/80" : "text-slate-500"}>
                    {active ? t("bts.answer_keys.close") : t("bts.answer_keys.open")}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>

        {opened && (
          <div
            id={panelId}
            role="region"
            aria-label={t("bts.answer_keys.opened_title", { code: opened.code })}
            aria-busy={opened.loading}
            className="rounded-xl border border-slate-200 bg-slate-50/60 p-4"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex min-w-0 flex-wrap items-center gap-2">
                <h4 className="text-base font-semibold tabular-nums text-slate-900">{t("bts.answer_keys.opened_title", { code: opened.code })}</h4>
                {opened.key?.preview && (
                  <Badge tone="violet" Icon={EyeIcon}>
                    {t("bts.print.preview_badge")}
                  </Badge>
                )}
              </div>
              <button
                type="button"
                onClick={closeKey}
                className={`${iconButtonClass} -mr-1 -mt-1 max-sm:h-11 max-sm:w-11`}
                aria-label={t("bts.answer_keys.close_aria", { code: opened.code })}
                title={t("buttons.close")}
              >
                <XMarkIcon className="h-5 w-5" aria-hidden="true" />
              </button>
            </div>
            {opened.loading ? (
              <p className="flex items-center gap-2 py-4 text-sm text-slate-500" role="status">
                <Spinner />
                {t("bts.answer_keys.loading")}
              </p>
            ) : opened.error ? (
              <div className="mt-3">
                <Notice
                  tone="error"
                  role="alert"
                  title={t("bts.answer_keys.load_error")}
                  actions={
                    <Button
                      type="button"
                      color="secondary-outline"
                      Icon={ArrowPathIcon}
                      text={t("bts.print.retry")}
                      onClick={() => askKey(opened.number, opened.code)}
                      className={phoneButtonClass}
                    />
                  }
                >
                  <p className="mt-1">{getApiErrorMessage(t, opened.error, "bts.answer_keys.load_error")}</p>
                </Notice>
              </div>
            ) : (
              <>
                {opened.key?.preview && <p className="mt-1 text-sm text-violet-800">{t("bts.answer_keys.preview_note")}</p>}
                <div className="mt-3 grid gap-3 grid-cols-[repeat(auto-fill,minmax(min(100%,15rem),1fr))]">
                  {(opened.key?.sections || []).map((section, index) => (
                    <KeySection key={`${section.heading}-${index}`} section={section} />
                  ))}
                </div>
              </>
            )}
          </div>
        )}
      </div>

      <ConfirmDialog
        isOpen={confirm.open}
        title={
          confirm.type === "table"
            ? t("bts.answer_keys.confirm_table_title")
            : t("bts.answer_keys.confirm_key_title", { code: confirm.code || "" })
        }
        confirmText={confirm.type === "table" ? t("buttons.download") : t("bts.answer_keys.open")}
        ConfirmIcon={confirm.type === "table" ? ArrowDownTrayIcon : KeyIcon}
        onConfirm={onConfirm}
        onCancel={() => setConfirm((prev) => ({ ...prev, open: false }))}
      >
        <p className="font-medium text-slate-900">
          {confirm.type === "table" ? t("bts.answer_keys.confirm_table_text") : t("bts.answer_keys.confirm_key_text")}
        </p>
        {!booklet.frozen && <p>{t(previewNoteKey(booklet, confirm.type === "table"))}</p>}
      </ConfirmDialog>
    </section>
  );
}

/**
 * A booklet's answer keys on its page: the key of each variant and the table of all keys (.xlsx), both written
 * to the log, each after a confirmation. `booklet` is the booklet's detail (GET exams/booklets/<uuid>/). Shown
 * once the booklet has sections.
 */
export default function BookletKeys({ booklet }) {
  if (!booklet?.uuid) return null;
  return <BookletKeysSection key={booklet.uuid} booklet={booklet} />;
}
